import { vellumApiSdk } from "@vellum/micro-app-auth";
import {
  Alert,
  Button,
  ConfirmDialog,
  getFriendlyDate,
  Loading,
  PageHeader,
  RecordTable,
  Select,
  useMutation,
  useQuery,
  type Column,
} from "@vellum/ui";
import type {
  AdminAnswerFirstRecipientSchema,
  AdminUserOption,
} from "@vellum/vellum-api";
import { useRef, useState } from "react";

const optionLabel = (user: AdminUserOption) => `${user.name} (${user.email})`;

export function AnswerFirstRecipientsPage() {
  const {
    data: recipients,
    loading,
    error: loadError,
    refetch,
  } = useQuery<AdminAnswerFirstRecipientSchema[]>(
    () => vellumApiSdk.adminListAnswerFirstRecipients(),
    "Something went wrong loading recipients.",
  );

  const [options, setOptions] = useState<AdminUserOption[]>([]);
  const [selected, setSelected] = useState<AdminUserOption | null>(null);
  const [searching, setSearching] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pendingRemoval, setPendingRemoval] =
    useState<AdminAnswerFirstRecipientSchema | null>(null);

  const add = useMutation(
    (userId: string) =>
      vellumApiSdk.adminCreateAnswerFirstRecipient({
        body: { user_id: userId },
      }),
    "Could not add that recipient.",
  );

  const remove = useMutation(
    (recipientId: string) =>
      vellumApiSdk.adminDeleteAnswerFirstRecipient({
        path: { recipient_id: recipientId },
      }),
    "Could not remove that recipient.",
  );

  const runSearch = (query: string) => {
    setSearching(true);
    void (async () => {
      const result = await vellumApiSdk.adminSearchUserOptions({
        query: { q: query || null },
      });
      setOptions(result.data ?? []);
      setSearching(false);
    })();
  };

  const handleInputChange = (input: string) => {
    if (debounce.current) {
      clearTimeout(debounce.current);
    }
    debounce.current = setTimeout(() => runSearch(input), 250);
  };

  const addRecipient = async () => {
    if (!selected) return;
    if (await add.mutate(selected.id)) {
      setSelected(null);
      await refetch();
    }
  };

  const removeRecipient = async () => {
    if (!pendingRemoval) return;
    const removed = await remove.mutate(pendingRemoval.id);
    setPendingRemoval(null);
    if (removed !== null) {
      await refetch();
    }
  };

  const columns: Column<AdminAnswerFirstRecipientSchema>[] = [
    { header: "Name", render: (row) => row.name },
    { header: "Email", render: (row) => row.email },
    {
      header: "Added",
      render: (row) => getFriendlyDate(row.created_at),
      className: "hidden md:table-cell",
    },
    {
      header: "",
      render: (row) => (
        <Button variant="outline" onClick={() => setPendingRemoval(row)}>
          Remove
        </Button>
      ),
    },
  ];

  const error = loadError ?? add.error ?? remove.error;

  return (
    <div>
      <PageHeader
        title="AnswerFirst Recipients"
        subtitle="Who gets emailed when an AnswerFirst call can't be turned into a job"
      />

      {error && <Alert className="mb-4">{error}</Alert>}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="w-full sm:max-w-md">
          <Select
            name="recipient-user"
            label="Add a recipient"
            isClearable
            // Server-side search already narrows the list; don't filter again.
            filterOption={() => true}
            options={options}
            value={selected}
            isLoading={searching}
            getOptionValue={(option: AdminUserOption) => option.id}
            getOptionLabel={optionLabel}
            onInputChange={handleInputChange}
            onChange={(option: AdminUserOption | null) => setSelected(option)}
            onMenuOpen={() => runSearch("")}
            placeholder="Search users by name or email…"
            noOptionsMessage={() => "No matching users"}
          />
        </div>
        <Button
          onClick={addRecipient}
          loading={add.loading}
          disabled={!selected}
        >
          Add
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loading />
        </div>
      ) : (
        <>
          {recipients?.length === 0 && (
            <Alert className="mb-4">
              No recipients configured — failure notifications are not being
              sent to anyone.
            </Alert>
          )}
          <RecordTable
            columns={columns}
            rows={recipients ?? []}
            rowKey={(row) => row.id}
            emptyMessage="No recipients configured."
          />
        </>
      )}

      <ConfirmDialog
        variant="delete"
        isOpen={pendingRemoval !== null}
        loading={remove.loading}
        onClose={() => setPendingRemoval(null)}
        onConfirm={removeRecipient}
        title={`Stop emailing ${pendingRemoval?.name ?? ""}?`}
      >
        <p className="text-sm text-gray-600">
          They will no longer be notified when AnswerFirst job creation fails.
        </p>
      </ConfirmDialog>
    </div>
  );
}
